import { useEffect, useState } from "react";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import { useCurrentSuperUser } from "../../../../reducers/layoutReducer/selectors";
import { AccountProduct, Product, ProductFamily } from "../types";

const useProductFamilies = () => {
  const superUser = useCurrentSuperUser() as { editAccountId: string | number };
  const [loading, setLoading] = useState(true);
  const [productFamilies, setProductFamilies] = useState<ProductFamily[]>([]);

  useEffect(() => {
    Promise.all([
      caseTransformingAxios
        .get(`/support/accounts/${superUser.editAccountId}/account_products/products`)
        .then(({ data }) => data as { [key: string]: Product[] }),
      caseTransformingAxios
        .get(`/support/accounts/${superUser.editAccountId}/account_products`)
        .then(({ data }) => data as AccountProduct[]),
    ])
      .then(([productFamilies, accountProducts]) => {
        setLoading(false);
        setProductFamilies(
          Object.entries(productFamilies).reduce((c, [productFamilyId, products]) => {
            const typeName = products[0]?.name.split(" - ")[0];
            c.push({
              typeName,
              id: productFamilyId,
              products,
              accountProducts: accountProducts.filter(
                (ap) => String(ap.product.details.productFamilyId) === String(productFamilyId),
              ),
            });
            return c;
          }, [] as ProductFamily[]),
        );
      })
      .catch(() => setLoading(false));
  }, [superUser.editAccountId]);

  return {
    actions: {
      upsertAccountProduct: (accountProduct: Partial<AccountProduct>) => {
        caseTransformingAxios[accountProduct.id ? "put" : "post"](
          `/support/accounts/${superUser.editAccountId}/account_products${accountProduct.id ? `/${accountProduct.id}` : ""}`,
          {
            accountProduct,
          },
        )
          .then(({ data }: { data: AccountProduct }) => {
            setProductFamilies((prevFamilies) =>
              prevFamilies.map((family) =>
                family.id === String(data.product.details.productFamilyId)
                  ? {
                      ...family,
                      accountProducts: accountProduct.id
                        ? family.accountProducts.map((ap) => (ap.id === data.id ? data : ap))
                        : [...family.accountProducts, data],
                    }
                  : family,
              ),
            );
          })
          .catch(() => {});
      },
      deleteAccountProduct: (accountProduct: AccountProduct) => {
        caseTransformingAxios
          .delete(`/support/accounts/${superUser.editAccountId}/account_products/${accountProduct.id}`)
          .then(() => {
            setProductFamilies((prevFamilies) =>
              prevFamilies.map((family) => ({
                ...family,
                accountProducts: family.accountProducts.filter((ap) => ap.id !== accountProduct.id),
              })),
            );
          })
          .catch(() => {});
      },
    },
    productFamilies,
    loading,
  };
};

export default useProductFamilies;
